import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Grid, Card, CardMedia, CardContent, IconButton, useTheme, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';
import { FaSpotify, FaApple } from 'react-icons/fa';
import notfamous from '../notfamous.webp';
import seattle from '../seattle.webp';
import cya from '../cya.jpeg';
import suburbs from '../suburbs.png';

const Music = () => {
  const theme = useTheme();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [openPopup, setOpenPopup] = useState(false);

  const handleClosePopup = () => setOpenPopup(false);

  // Auto-open popup after 7 seconds
  useEffect(() => {
    // Check if the page is being displayed in an iframe/embed
    const isEmbedded = window.location.search.includes('embed=true') || window.self !== window.top;

    // Don't show popup if embedded
    if (isEmbedded) {
      return;
    }

    const timer = setTimeout(() => {
      setOpenPopup(true);
    }, 4000);

    // Cleanup timer on component unmount
    return () => clearTimeout(timer);
  }, []);

  // Placeholder data - you'll replace with real links
  const releases = [
    { title: 'Newest Release', songName: 'CYA', image: cya, link: 'https://open.spotify.com/track/4UOqzs2SVh4lC72HpvlklE?si=bd26a6db74594318' },
    { title: 'Hottest Song', songName: 'Seattle', image: seattle, link: 'https://open.spotify.com/track/69HFxctxc0HxOyTMwsVlGz' },
    { title: 'Most Streamed', songName: 'Stuck In The Suburbs', image: suburbs, link: 'https://open.spotify.com/track/6cSGaWVVYCGGIwgh5XKbXR' },
  ];

  return (
    <Box
      id="music"
      ref={ref}
      sx={{
        minHeight: '100vh',
        py: 10,
        backgroundColor: '#0a0a0a',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 2,
              fontSize: { xs: '2.5rem', md: '4rem' },
              color: '#FFFFFF',
            }}
          >
            LISTEN
          </Typography>
          <Box
            sx={{
              width: '100px',
              height: '4px',
              backgroundColor: theme.palette.primary.main,
              margin: '0 auto 6rem',
            }}
          />
        </motion.div>

        {/* Releases Grid */}
        <Grid container spacing={4} sx={{ mb: 8, justifyContent: 'center' }}>
          {releases.map((release, index) => (
            <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }}
                style={{ width: '100%', maxWidth: '300px' }}
              >
                <Card
                  component="a"
                  href={release.link}
                  target={release.link !== '#' ? '_blank' : undefined}
                  rel={release.link !== '#' ? 'noopener noreferrer' : undefined}
                  sx={{
                    backgroundColor: '#121212',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    display: 'block',
                    maxWidth: '300px',
                    margin: '0 auto',
                    '&:hover .overlay': {
                      opacity: 1,
                    },
                    '&:hover': {
                      transform: 'translateY(-10px)',
                    },
                    transition: 'transform 0.3s',
                  }}
                >
                  <CardMedia
                    component="div"
                    sx={{
                      width: '100%',
                      paddingTop: '100%',
                      backgroundColor: '#1a1a1a',
                      position: 'relative',
                      backgroundImage: release.image === notfamous ? `url(${notfamous})` : 
                                      release.image === seattle ? `url(${seattle})` : 
                                      release.image === cya ? `url(${cya})` : 
                                      release.image === suburbs ? `url(${suburbs})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                    }}
                  >
                  </CardMedia>
                  <Box
                    className="overlay"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 157, 0, 0.9)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      transition: 'opacity 0.3s',
                    }}
                  >
                    <PlayArrowIcon sx={{ fontSize: '4rem', color: '#FFFFFF' }} />
                  </Box>
                  <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                    <Typography 
                      variant="overline" 
                      sx={{ 
                        color: theme.palette.primary.main, 
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: '0.15em',
                        display: 'block',
                        mb: 0.5
                      }}
                    >
                      {release.title}
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: '#FFFFFF', 
                        fontWeight: 700,
                        fontSize: '1.1rem'
                      }}
                    >
                      {release.songName}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Spotify Embed */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <Box sx={{ mb: 8, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ color: theme.palette.primary.main, mb: 3 }}>
              OUR MUSIC
            </Typography>
            <Box
              sx={{
                maxWidth: '600px',
                margin: '0 auto',
                borderRadius: 2,
                overflow: 'hidden',
                border: `2px solid ${theme.palette.primary.main}`,
              }}
            >
              <iframe
                title="Spotify Player"
                style={{ borderRadius: '12px' }}
                src="https://open.spotify.com/embed/artist/4MyemXDuB1bsOb4MO3lze3?utm_source=generator&theme=0"
                width="100%"
                height="425"
                frameBorder="0"
                allowFullScreen=""
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
              ></iframe>
            </Box>
          </Box>
        </motion.div>

        {/* Streaming Platforms */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 3 }}>
              STREAM ON
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <IconButton
                component="a"
                href="https://open.spotify.com/artist/4MyemXDuB1bsOb4MO3lze3"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#FFFFFF',
                  border: `2px solid ${theme.palette.primary.main}`,
                  padding: 2,
                  '&:hover': {
                    color: theme.palette.primary.light,
                    borderColor: theme.palette.primary.light,
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.3s',
                }}
              >
                <FaSpotify size={40} />
              </IconButton>
              <IconButton
                component="a"
                href="https://music.apple.com/us/artist/boxrooster/1793755446"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#FFFFFF',
                  border: `2px solid ${theme.palette.primary.main}`,
                  padding: 2,
                  '&:hover': {
                    color: theme.palette.primary.light,
                    borderColor: theme.palette.primary.light,
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.3s',
                }}
              >
                <FaApple size={40} />
              </IconButton>
            </Box>
          </Box>
        </motion.div>

        {/* Popup Modal for Presave */}
        <Dialog
          open={openPopup}
          onClose={handleClosePopup}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#121212',
              borderRadius: '16px',
              border: `2px solid ${theme.palette.primary.main}`,
              boxShadow: `0 8px 40px ${theme.palette.primary.main}40`,
              margin: { xs: 2, sm: 4 },
              maxWidth: { xs: '95%', sm: '500px' },
            }
          }}
        >
          <DialogTitle
            sx={{
              textAlign: 'center',
              color: '#FFFFFF',
              fontSize: { xs: '1.5rem', sm: '2rem' },
              fontWeight: 'bold',
              pb: 2,
              pt: { xs: 3, sm: 4 },
              pr: { xs: 5, sm: 3 },
              position: 'relative',
            }}
          >
            Presave our new song Movie Scenes
            <IconButton
              onClick={handleClosePopup}
              sx={{
                position: 'absolute',
                right: { xs: 4, sm: 8 },
                top: { xs: 4, sm: 8 },
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main + '20',
                  transform: 'rotate(90deg)',
                },
                transition: 'all 0.3s',
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              px: { xs: 3, sm: 4 },
              pb: 4,
              pt: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              component="a"
              href="https://ffm.to/moviescenessingle"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClosePopup}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: '#FFFFFF',
                padding: { xs: '12px 32px', sm: '16px 48px' },
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: { xs: '1rem', sm: '1.2rem' },
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s',
                '&:hover': {
                  backgroundColor: theme.palette.primary.light,
                  transform: 'scale(1.05)',
                  boxShadow: `0 4px 20px ${theme.palette.primary.main}60`,
                },
              }}
            >
              Presave
            </Box>
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Music;
